import React, { useEffect, lazy, Suspense } from 'react'
import { connect } from 'react-redux'
import Login from './login'
import Landing from './landing'
import { ToastContainer } from 'react-toastify'
import { subscriptionPlansFetchRequest } from '../../action/subscription-plan-actions'
import SplashScreen from './splash-screen'
import isNil from 'lodash/isNil'
import { logout } from '../../action/auth-actions'
import { collapseLeftToolbar } from '../../action/left-toolbar-state-actions'
import { enableMapObject } from '../../action/disable-map-obj-state-action'
import Merchant from './merchant-modals'
import DeviceLink from './device-link'
import OAuthConsent from './oauth-consent'
import ConnectedApps from '../connected-apps'
import ArtemisCredits from '../artemis-credits'
import { Route, Routes, Navigate } from 'react-router-dom'
import PasswordReset from './login/password-reset'
import { setLoading } from '../../action/loading-actions'
import * as Sentry from '@sentry/react'

const GoogleThreeD = lazy(() => import('../threedmap/threedemap'))

const sentryDsn = __SENTRY_DSN__
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: __NODE_ENV__,
    release: __SENTRY_RELEASE__,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      Sentry.httpClientIntegration({ failedRequestStatusCodes: [[402, 599]] }),
    ],
    tracesSampleRate: 0.1,
    sendDefaultPii: true,

    beforeSend(event, hint) {
      try {
        const error = hint?.originalException
        const message =
          typeof event?.message === 'string'
            ? event.message
            : typeof error?.message === 'string'
              ? error.message
              : ''

        if (
          message.includes('idpiframe_initialization_failed') ||
          message.includes('Not a valid origin') ||
          message.includes('string ref') ||
          message.includes('Force close delete origin') ||
          message.includes('Connection is closing') ||
          message.includes('[webpack-dev-server]') ||
          message.includes('HTTP Client Error with status code: 401') ||
          message.includes('redux-persist: rehydrate') ||
          message.includes('memory access out of bounds')
        ) {
          return null
        }

        if (error instanceof ReferenceError || error instanceof TypeError) return event
        if (event.level === 'fatal' || event.exception) return event
        if (event.request) return event

        return event
      } catch {
        return event
      }
    },
  })
}

function App({
  auth,
  user,
  subscriptionPlans,
  collapseLeftToolbar,
  logout,
  subscriptionPlansFetchRequest,
  enableMapObject,
  setLoading,
}) {
  useEffect(() => {
    if (window.location.pathname === '/password-reset') {
      logout()
      setLoading(false)
    }
    collapseLeftToolbar()
    enableMapObject()
    if (subscriptionPlans.length === 0) {
      subscriptionPlansFetchRequest()
    }
  }, [])

  useEffect(() => {
    if (!user || Object.keys(user).length === 0) {
      Sentry.setUser(null)
    } else {
      Sentry.setUser({
        id: user?.id,
        email: user?.email,
        username: user?.fullName,
      })
    }
  }, [user])

  const isAuthenticated = !isNil(auth) && !isNil(auth.token)

  return (
    <div className="app">
      <SplashScreen />
      <ToastContainer autoClose={5000} closeButton={false} position="bottom-center" theme="dark" />
      <Suspense fallback={null}>
        <Routes>
          <Route path="password-reset" element={<PasswordReset />} />
          <Route path="claim" element={isAuthenticated ? <Merchant /> : <Navigate to="/login" />} />
          <Route path="redeem" element={isAuthenticated ? <Merchant /> : <Navigate to="/login" />} />
          {/*
            Device pairing approval (WEB-7089). Renders <Login /> in place rather than
            redirecting, which is what keeps ?code= in the URL across a sign-in: the two routes
            below use the same pattern and the browser never navigates, so the route simply flips
            once the token lands in redux. The claim/redeem pattern above must not be copied here
            - <Navigate to="/login" /> hits the catch-all and drops the query string, which would
            strand the user and leave the car polling until the code expires.
          */}
          <Route path="link" element={isAuthenticated ? <DeviceLink /> : <Login />} />
          {/*
            OAuth sign-in and consent (WEB-7106): the same in-place <Login /> as /link, so
            ?oauthRequest= survives sign-in and sign-up and the user returns to the same request.
          */}
          <Route path="oauth/consent" element={isAuthenticated ? <OAuthConsent /> : <Login />} />
          {/* Connected apps (WEB-7107): the same in-place <Login />, for links from outside the app. */}
          <Route path="connected-apps" element={isAuthenticated ? <ConnectedApps /> : <Login />} />
          <Route path="/" element={isAuthenticated ? <Landing /> : <Login />} />
          {/*
            "Open in BaseMap" map deep links (WEB-7109): /map?bbox=&layers=&marker=... from the MCP
            server. The same in-place <Login /> as /link, so the link survives sign-in; without this
            route the catch-all would send /map to / and drop the query string.
          */}
          <Route path="map" element={isAuthenticated ? <Landing /> : <Login />} />
          {/*
            Artemis group chat invite links: /artemis?invite=TOKEN. The same in-place <Login />, so the token
            survives sign-in; without this route the catch-all sends /artemis to / and drops it.
          */}
          <Route path="artemis" element={isAuthenticated ? <Landing /> : <Login />} />
          {/* Artemis credit packs (WEB-7135): Artemis's 429 BuyCreditsUrl and Stripe Checkout's return. */}
          <Route path="artemis/credits" element={isAuthenticated ? <ArtemisCredits /> : <Login />} />
          <Route path="/three-d-maps" element={isAuthenticated ? <GoogleThreeD /> : <Login />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </div>
  )
}

const mapStateToProps = state => ({
  auth: state.auth,
  user: state.user,
  subscriptionPlans: state.subscriptionPlans,
})

const mapDispatchToProps = dispatch => ({
  collapseLeftToolbar: () => dispatch(collapseLeftToolbar()),
  logout: () => dispatch(logout()),
  subscriptionPlansFetchRequest: () => dispatch(subscriptionPlansFetchRequest()),
  enableMapObject: () => dispatch(enableMapObject()),
  setLoading: payload => dispatch(setLoading(payload)),
})

export default connect(mapStateToProps, mapDispatchToProps)(App)
